import { useState, useEffect, useCallback } from 'react';
import { FiPrinter, FiX } from 'react-icons/fi';
import API from '../../shared/api';

const esc = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

function buildConteoHtml({ empresa, sedeLabel, productos }) {
  const rows = productos
    .map(
      (p, i) =>
        `<tr><td class="num">${i + 1}</td><td class="cod">${esc(p.codigo)}</td><td>${esc(p.descripcion)}</td><td class="check">&#9634;</td><td class="cant"></td></tr>`
    )
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>Conteo de inventario</title>
<style>
@page { size: A4 portrait; margin: 10mm; }
body { font-family: Arial, sans-serif; font-size: 10px; color: #000; }
h1 { font-size: 15px; margin: 0 0 2px; }
.meta { font-size: 10px; margin-bottom: 8px; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 4px 5px; text-align: left; border-bottom: 1px solid #ccc; }
th { border-top: 2px solid #000; border-bottom: 1px solid #000; font-size: 9px; text-transform: uppercase; }
td.num { width: 24px; color: #666; }
td.cod { font-family: 'Courier New', monospace; width: 90px; }
td.check { width: 30px; text-align: center; font-size: 12px; }
td.cant { width: 60px; border-left: 1px solid #ccc; }
</style></head><body>
<h1>Conteo físico de inventario</h1>
<div class="meta">${esc(empresa || '')}${sedeLabel ? ' · ' + esc(sedeLabel) : ''} · ${productos.length} artículos</div>
<table>
<thead><tr><th class="num">#</th><th class="cod">Código</th><th>Descripción</th><th class="check">OK</th><th class="cant">Cantidad contada</th></tr></thead>
<tbody>${rows}</tbody>
</table>
</body></html>`;
}

export default function ConteoInventarioModal({ sede, sedeLabel, empresa, onClose }) {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setCargando(true);
    setError(null);
    API.get(`/inventario/imprimir-precios?sede=${encodeURIComponent(sede || 'todas')}`)
      .then((r) => {
        if (!mounted) return;
        setProductos(r.data?.data?.productos || []);
      })
      .catch((e) => mounted && setError(e.response?.data?.detail || e.message))
      .finally(() => mounted && setCargando(false));
    return () => {
      mounted = false;
    };
  }, [sede]);

  const imprimir = useCallback(() => {
    const html = buildConteoHtml({ empresa, sedeLabel, productos });
    let win = null;
    try {
      win = window.open('', '_blank', 'width=800,height=900');
    } catch {
      win = null;
    }
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.addEventListener('load', () => setTimeout(() => { try { win.focus(); win.print(); } catch (e) {} }, 250));
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:800px;height:900px;border:0;background:#fff;';
    document.body.appendChild(iframe);
    const fdoc = iframe.contentWindow.document;
    fdoc.open();
    fdoc.write(html);
    fdoc.close();
    iframe.addEventListener('load', () => setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 250));
    setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 30000);
  }, [empresa, sedeLabel, productos]);

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', padding: 20,
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 18, width: 720, maxWidth: '96%', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>Lista para conteo físico</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{empresa || ''}{sedeLabel ? ` · ${sedeLabel}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {cargando && <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>Cargando...</div>}
          {error && <div style={{ padding: 20, color: '#c5221f', fontSize: 13 }}>{error}</div>}
          {!cargando && !error && (
            <>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
                Se imprime una planilla con una casilla de verificación y un espacio para anotar la cantidad
                contada de cada artículo. Ideal para arqueo físico.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #000' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px', width: 24 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px', width: 90, fontFamily: 'monospace' }}>Código</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descripción</th>
                    <th style={{ textAlign: 'center', padding: '4px 6px', width: 40 }}>OK</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px', width: 100 }}>Contado</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map((p, i) => (
                    <tr key={p.codigo} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '3px 6px', color: '#666' }}>{i + 1}</td>
                      <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>{p.codigo}</td>
                      <td style={{ padding: '3px 6px' }}>{p.descripcion}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: 14 }}>&#9634;</td>
                      <td style={{ padding: '3px 6px' }}>&nbsp;</td>
                    </tr>
                  ))}
                  {productos.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>Sin productos</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #e8eaef', flexShrink: 0 }}>
          <button
            onClick={imprimir}
            disabled={cargando || !!error || productos.length === 0}
            style={{
              width: '100%', padding: 12, borderRadius: 12, border: 'none', background: '#2563eb',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: cargando || !!error || productos.length === 0 ? 0.5 : 1,
            }}
          >
            <FiPrinter size={16} /> Imprimir planilla de conteo
          </button>
        </div>
      </div>
    </div>
  );
}
